import React from 'react';
import PropTypes from 'prop-types';
import { useStaticQuery, graphql } from 'gatsby';
import { Helmet } from 'react-helmet';
import { useLocation } from '@reach/router';

import fontRegular from '@src/assets/fonts/roboto/Roboto-Regular.ttf';
import fontBold from '@src/assets/fonts/roboto/Roboto-Bold.ttf';
import fontThin from '@src/assets/fonts/roboto/Roboto-Thin.ttf';

const SEO = ({
  noIndex = false,
  title,
  seoTitle,
  description,
  seoDescription,
  date,
  pageType = 'article',
  featuredImage,
  schemaMarkup,
}) => {
  const { pathname } = useLocation();
  const { site } = useStaticQuery(query);

  const { defaultTitle, titleTemplate, defaultDescription, siteURL } = site.siteMetadata;

  const seo = {
    title: seoTitle || title || defaultTitle,
    description: seoDescription || description || defaultDescription,
    url: `${siteURL}${pathname}`,
    image: featuredImage,
    date: date,
    schemaMarkup: schemaMarkup,
  };

  const fullPageUrl = typeof window !== 'undefined' ? window.location.href : null;
  const isUrlWithPagination = /\?page=\d/g.test(fullPageUrl);

  return (
    <Helmet>
      {/* Preload fonts */}
      <link rel="preload" as="font" href={fontRegular} type="font/ttf" crossOrigin="anonymous" />
      <link rel="preload" as="font" href={fontBold} type="font/ttf" crossOrigin="anonymous" />
      <link rel="preload" as="font" href={fontThin} type="font/ttf" crossOrigin="anonymous" />

      {isUrlWithPagination && <link rel="canonical" href={fullPageUrl} />}

      <meta name="description" content={seo.description} />
      <meta name="image" content={seo.image} />
      <meta httpEquiv="Pragma" content="no-cache" />
      <meta httpEquiv="Expires" content="-1" />
      <meta httpEquiv="CACHE-CONTROL" content="NO-CACHE" />
      <meta name="facebook-domain-verification" content="wiil559ycz4oqo5ms3cz1efjzqt74h" />
      {noIndex && <meta name="robots" content="noindex" />}
      <title>{seo.title}</title>

      {seo.date && <meta property="article:published_time" content={seo.date} />}

      {/* Opengraph */}
      {seo.url && <meta property="og:url" content={seo.url} />}
      <meta property="og:type" content={pageType} />
      {seo.title && <meta property="og:title" content={seo.title} />}
      {seo.description && <meta property="og:description" content={seo.description} />}
      {seo.image && <meta property="og:image" content={seo.image} />}
      {seo.image && <meta property="og:image:secure_url" content={seo.image} />}
      <meta property="og:site_name" content={titleTemplate} />
      {/* Twitter meta */}
      <meta name="twitter:card" content="summary_large_image" />
      {seo.title && <meta name="twitter:title" content={seo.title} />}
      {seo.description && <meta name="twitter:description" content={seo.description} />}
      {seo.image && <meta property="twitter:image" content={seo.image} />}
      {/* Google */}
      {seo.image && <meta itemProp="thumbnailUrl" content={seo.image} />}
      {seo.image && <meta itemProp="image" content={seo.image} />}
      {seo.image && <meta itemProp="imageUrl" content={seo.image} />}
      {seo.schemaMarkup && <script type="application/ld+json">{JSON.stringify(seo.schemaMarkup)}</script>}
    </Helmet>
  );
};

export default SEO;

SEO.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
  article: PropTypes.bool,
};

SEO.defaultProps = {
  title: null,
  description: null,
  image: null,
  article: false,
};

const query = graphql`
  query SEO {
    site {
      siteMetadata {
        defaultTitle: title
        titleTemplate
        defaultDescription: description
        siteURL: url
      }
    }
  }
`;
